import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeader from "./PageHeader";

// Icons
const SaveIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
const DropIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>;
const AlertIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;

const getFatPriceKey = (title) => `${title}_fatPrices`;

const AnimalPriceGrid = ({ tableTitle }) => {
  const isCow = tableTitle.toLowerCase().includes("cow");

  // Matrix Constants
  const startFat = 3.5, endFat = 10.0;
  const startSnf = 7.0, endSnf = 10.0;
  const numCols = Math.round((endFat - startFat) / 0.1) + 1;
  const numRows = Math.round((endSnf - startSnf) / 0.1) + 1;

  const [prices, setPrices] = useState(() => JSON.parse(localStorage.getItem(tableTitle)) || Array.from({ length: numRows }, () => Array(numCols).fill("")));
  const [fatPrices, setFatPrices] = useState(() => {
    const stored = localStorage.getItem(getFatPriceKey(tableTitle));
    return stored ? JSON.parse(stored) : isCow ? { below35: "", above35: "" } : { fat100: "" };
  });
  const [priceFat100, setPriceFat100] = useState("");
  const [priceSnf100, setPriceSnf100] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges]);

  const handleSave = () => {
    localStorage.setItem(tableTitle, JSON.stringify(prices));
    localStorage.setItem(getFatPriceKey(tableTitle), JSON.stringify(fatPrices));
    setHasChanges(false);
    toast.success("Saved Successfully!");
  };

  const generateTable = () => {
    if (!priceFat100 || !priceSnf100) return toast.error("Enter Fat & SNF rates");
    const generated = Array.from({ length: numRows }, (_, y) => {
      const snfValue = startSnf + y * 0.1;
      return Array.from({ length: numCols }, (_, x) => {
        const fatValue = startFat + x * 0.1;
        return ((fatValue * parseFloat(priceFat100)) / 10 + (snfValue * parseFloat(priceSnf100)) / 10).toFixed(2);
      });
    });
    setPrices(generated);
    setHasChanges(true);
    toast.info("Matrix Updated! Click Save to apply.");
  };

  const updateFatPrice = (updates) => {
    setFatPrices(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24 w-full">
      <ToastContainer position="top-center" autoClose={1000} hideProgressBar />

      {/* HEADER */}
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 px-4 py-3">
        <div className="flex items-center justify-between max-w-4xl mx-auto w-full">
          <div className="flex flex-col">
            <PageHeader title={isCow ? "Cow Rate" : "Buffalo Rate"} />
            {hasChanges && (
              <span className="flex items-center gap-1 text-[10px] font-bold text-orange-600 animate-pulse uppercase tracking-tighter mt-0.5">
                <AlertIcon /> Unsaved Changes
              </span>
            )}
          </div>
          <button onClick={handleSave} className={`flex-shrink-0 p-3 rounded-2xl shadow-lg active:scale-90 transition-all ${hasChanges ? 'bg-orange-600 ring-4 ring-orange-100' : 'bg-indigo-600'} text-white`}>
            <SaveIcon />
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 sm:px-6 mt-4 space-y-6">

        {/* FAT INPUTS */}
        <section className="bg-white rounded-3xl shadow-sm border-2 border-orange-100 overflow-hidden">
          <div className="bg-orange-500 px-4 py-3 flex items-center gap-2">
            <DropIcon />
            <span className="text-white font-black text-xs uppercase tracking-widest">Main Fat Pricing</span>
          </div>
          <div className="p-5 grid grid-cols-1 gap-4">
            {isCow ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FatInput label="Fat < 3.5 Rate" value={fatPrices.below35} color="orange" onChange={(v) => updateFatPrice({ below35: v })} />
                <FatInput label="Fat > 3.5 Rate" value={fatPrices.above35} color="orange" onChange={(v) => updateFatPrice({ above35: v })} />
              </div>
            ) : (
              <FatInput label="Standard 100% Fat Price" value={fatPrices.fat100} color="orange" onChange={(v) => updateFatPrice({ fat100: v })} />
            )}
          </div>
        </section>

        {/* MATRIX GENERATOR */}
        <section className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4">Bulk Matrix Generator</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FatInput label="100% Fat" value={priceFat100} onChange={setPriceFat100} color="indigo" />
            <FatInput label="100% SNF" value={priceSnf100} onChange={setPriceSnf100} color="indigo" />
            <button onClick={generateTable} className="w-full py-4 bg-gray-900 text-white rounded-2xl font-bold text-sm active:bg-black transition-colors">Generate Preview</button>
          </div>
        </section>

        {/* TABLE */}
        <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
          <div className="p-4 bg-gray-50 border-b flex justify-between items-center">
            <span className="text-[10px] font-bold text-gray-400 uppercase italic">Swipe Table Left/Right →</span>
            <div className="flex gap-3 text-[9px] font-bold">
              <span className="text-indigo-600">FAT →</span>
              <span className="text-gray-400">SNF ↓</span>
            </div>
          </div>

          {/* FIXED SCROLLABLE TABLE WRAPPER */}
          <div className="overflow-x-auto w-full -mx-3 px-3 touch-pan-x" style={{ WebkitOverflowScrolling: "touch" }}>
            <table className="min-w-max w-full text-[11px] border-collapse">
              <thead className="sticky top-0 bg-white z-20">
                <tr className="bg-gray-100">
                  <th className="p-3 border-r border-b text-gray-500 sticky left-0 bg-gray-100 z-30 font-bold">SNF</th>
                  {Array.from({ length: numCols }).map((_, i) => (
                    <th key={i} className="p-3 border-b text-indigo-600 font-bold text-center">{(startFat + i * 0.1).toFixed(1)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {prices.map((row, y) => (
                  <tr key={y} className="hover:bg-gray-50 transition-colors border-b last:border-0">
                    <td className="p-3 border-r bg-gray-50 font-bold text-gray-700 sticky left-0 z-10 text-center shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]">
                      {(startSnf + y * 0.1).toFixed(1)}
                    </td>
                    {row.map((price, x) => (
                      <td key={x} className="p-3 text-gray-600 text-center min-w-[70px]">{price ? `₹${price}` : '-'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

const FatInput = ({ label, value, onChange, color = "indigo", placeholder = "0.00" }) => {
  const borderColors = {
    orange: "focus:border-orange-500 focus:ring-orange-500/20 text-orange-700",
    indigo: "focus:border-indigo-500 focus:ring-indigo-500/20 text-indigo-700",
  };

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-tight ml-1">{label}</label>
      <div className="relative">
        <span className={`absolute left-4 top-1/2 -translate-y-1/2 font-bold text-sm ${borderColors[color].split(' ')[2]}`}>₹</span>
        <input type="number" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} className={`w-full pl-9 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl text-sm font-bold focus:outline-none focus:ring-4 transition-all ${borderColors[color]}`} placeholder={placeholder} />
      </div>
    </div>
  );
};

export default AnimalPriceGrid;
